import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Tickets from "@/pages/Tickets";
import Estimates from "@/pages/Estimates";
import Team from "@/pages/Team";
import Schedule from "@/pages/Schedule";
import MapPage from "@/pages/Map";
import Inbox from "@/pages/Inbox";
import Purchasing from "@/pages/Purchasing";
import Properties from "@/pages/Properties";
import Financials from "@/pages/Financials";
import Finance from "@/pages/Finance";
import Subcontractors from "@/pages/Subcontractors";
import Equipment from "@/pages/Equipment";
import SubPayments from "@/pages/SubPayments";
import Materials from "@/pages/Materials";
import Suppliers from "@/pages/Suppliers";
import TimeClock from "@/pages/TimeClock";
import Receivables from "@/pages/Receivables";
import Payroll from "@/pages/Payroll";
import Crew from "@/pages/Crew";
import Clients from "@/pages/Clients";
import Settings from "@/pages/Settings";
import MaterialRequests from "@/pages/MaterialRequests";
import MaterialTrendsPage from "@/pages/MaterialTrends";
import ProjectsPage from "@/pages/Projects";
import PermissionsPage from "@/pages/Permissions";
import HR from "@/pages/HR";
import Help from "@/pages/Help";
import PublicApproval from "@/pages/PublicApproval";
import GeofenceGuard from "@/components/GeofenceGuard";

const Protected = ({ children, roles, permission }) => {
  const { user, loading, can } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  if (permission && !can(permission.module, permission.action || "view")) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

const LoginRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (user) {
    const dest = user.role === "technician" ? "/timeclock" : (user.role === "subcontractor" ? "/tickets" : "/");
    return <Navigate to={dest} replace />;
  }
  return <Login />;
};

const Home = () => {
  const { user } = useAuth();
  if (user.role === "technician") return <Navigate to="/timeclock" replace />;
  if (user.role === "subcontractor") return <Navigate to="/tickets" replace />;
  return <Dashboard />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <Toaster position="top-right" richColors />
            <GeofenceGuard />
            <Routes>
              {/* Public routes (no auth) */}
              <Route path="/approve-project/:token" element={<PublicApproval />} />
              <Route path="/login" element={<LoginRoute />} />
              <Route path="/" element={<Protected><Home /></Protected>} />
              <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
              <Route path="/schedule" element={<Protected roles={["admin", "manager", "pm", "technician"]}><Schedule /></Protected>} />
              <Route path="/map" element={<Protected roles={["admin", "manager", "pm", "technician"]}><MapPage /></Protected>} />
              <Route path="/inbox" element={<Protected roles={["admin", "manager"]}><Inbox /></Protected>} />
              <Route path="/purchasing" element={<Protected roles={["admin", "manager", "purchasing"]}><Purchasing /></Protected>} />
              <Route path="/financials" element={<Protected permission={{ module: "financials", action: "view" }}><Financials /></Protected>} />
              <Route path="/finance" element={<Protected permission={{ module: "financials", action: "view" }}><Finance /></Protected>} />
              <Route path="/subcontractors" element={<Protected roles={["admin", "manager", "estimator", "pm"]}><Subcontractors /></Protected>} />
              <Route path="/equipment" element={<Protected roles={["admin", "manager", "pm", "purchasing"]}><Equipment /></Protected>} />
              <Route path="/payments" element={<Protected roles={["admin", "manager"]}><SubPayments /></Protected>} />
              <Route path="/materials" element={<Protected roles={["admin", "manager", "purchasing"]}><Materials /></Protected>} />
              <Route path="/suppliers" element={<Protected roles={["admin", "manager", "purchasing"]}><Suppliers /></Protected>} />
              <Route path="/timeclock" element={<Protected roles={["admin", "manager", "technician"]}><TimeClock /></Protected>} />
              <Route path="/payroll" element={<Protected roles={["admin", "manager"]}><Payroll /></Protected>} />
              <Route path="/estimates" element={<Protected><Estimates /></Protected>} />
              <Route path="/receivables" element={<Protected roles={["admin", "manager"]}><Receivables /></Protected>} />
              <Route path="/units" element={<Protected><Properties /></Protected>} />
              <Route path="/properties" element={<Protected><Properties /></Protected>} />
              <Route path="/team" element={<Protected roles={["admin", "manager"]}><Team /></Protected>} />
              <Route path="/crew" element={<Protected permission={{ module: "crew", action: "view" }}><Crew /></Protected>} />
              <Route path="/clients" element={<Protected roles={["admin", "manager"]}><Clients /></Protected>} />
              <Route path="/settings" element={<Protected roles={["admin", "manager"]}><Settings /></Protected>} />
              <Route path="/permissions" element={<Protected roles={["admin"]}><PermissionsPage /></Protected>} />
              <Route path="/material-requests" element={<Protected roles={["admin", "manager", "pm", "purchasing", "director", "technician"]}><MaterialRequests /></Protected>} />
              <Route path="/material-trends" element={<Protected roles={["admin", "manager", "purchasing", "financeiro", "analista"]}><MaterialTrendsPage /></Protected>} />
              <Route path="/projects" element={<Protected roles={["admin", "manager", "pm", "estimator", "director", "financeiro", "analista", "client"]}><ProjectsPage /></Protected>} />
              <Route path="/hr" element={<Protected roles={["admin", "rh", "manager", "financeiro"]}><HR /></Protected>} />
              <Route path="/help" element={<Protected><Help /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
